import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import Styles from "../BusinessDescriptionForm.module.css";

// SVGs
import Upload from "../../../../assets/SVGs/upload.svg";
import Delete from "../../../../assets/SVGs/delete_icon.svg";
import { apiService } from "../../../../api/apiService";

interface JobHierarchyUploadStepProps {
  setSelectedFile: React.Dispatch<
    React.SetStateAction<
      Record<string, { file: File | null; metadata: string[] }>
    >
  >;
  setIsFileSelected: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFile: Record<string, { file: File | null; metadata: string[] }>;
  isEditMode?: boolean;
  jobHierarchyData?: any | null;
  setIsLoadingPreview?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedUpload?: React.Dispatch<React.SetStateAction<boolean>>;
}

const JobHierarchyUploadStep: React.FC<JobHierarchyUploadStepProps> = ({
  setSelectedFile,
  setIsFileSelected,
  selectedFile,
  isEditMode = false,
  jobHierarchyData,
  setIsLoadingPreview,
  setSelectedUpload,
}) => {
  const [fileType, setFileType] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreviewLocal] = useState(false);

  useEffect(() => {
    if (setIsLoadingPreview) {
      setIsLoadingPreview(isLoadingPreview);
    }
  }, [isLoadingPreview, setIsLoadingPreview]);

  const getFileTypeFromName = (fileName: string): string => {
    const parts = fileName.split(".");
    return parts.length > 1 ? parts.pop()!.toLowerCase() : "unknown";
  };

  const isImageFile = (type: string) => ["png", "jpg", "jpeg"].includes(type);
  const isPdfFile = (type: string) => type === "pdf";
  const isOfficeFile = (type: string) =>
    ["pptx", "docx", "doc", "ppt", "xlsx", "xls", "xlsm"].includes(type);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const triggerFileUpload = () => {
    const fileInput = document.getElementById("fileUpload") as HTMLInputElement;
    fileInput?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileExtension = getFileTypeFromName(file.name);

    if (
      !isImageFile(fileExtension) &&
      !isPdfFile(fileExtension) &&
      !isOfficeFile(fileExtension)
    ) {
      alert(
        "Unsupported file type. Please upload JPEG, JPG, PNG, PDF, PPTX, DOCX, DOC, PPT, XLSX, XLSM or XLS",
      );
      return;
    }

    setFileType(fileExtension);
    setIsFileSelected(true);

    if (isImageFile(fileExtension) || isPdfFile(fileExtension)) {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrl(fileReader.result as string);
        setSelectedFile({
          [file.name]: { file, metadata: [] },
        });
        if (setSelectedUpload) setSelectedUpload(true);
      };
      fileReader.readAsDataURL(file);
    } else {
      // ✅ Office file support: Save file even without preview
      setPreviewUrl(null);
      setSelectedFile({
        [file.name]: { file, metadata: [] },
      });
      if (setSelectedUpload) setSelectedUpload(true);
    }
  };

  const handleDeleteFile = () => {
    setPreviewUrl(null);
    setFileType(null);
    setSelectedFile({});
    setIsFileSelected(false);
    if (setSelectedUpload) setSelectedUpload(false);
  };

  useEffect(() => {
    const fetchFilePreview = async () => {
      if (!jobHierarchyData?.doc_path) return;

      setIsLoadingPreviewLocal(true);
      try {
        const response = await apiService(
          `/download?doc_path=${encodeURIComponent(jobHierarchyData.doc_path)}`,
          "GET",
        );

        const data = response;

        if (data.status === "success" && data.data) {
          const fileExtension = getFileTypeFromName(jobHierarchyData.file_name);
          setFileType(fileExtension);

          let url = "";
          if (isPdfFile(fileExtension)) {
            url = `data:application/pdf;base64,${data.data}`;
          } else if (isImageFile(fileExtension)) {
            url = `data:image/${fileExtension};base64,${data.data}`;
          }

          setPreviewUrl(url);
          setIsFileSelected(true);
        }
      } catch (error) {
        console.error("Error fetching file preview:", error);
        const fileExtension = getFileTypeFromName(jobHierarchyData.file_name);
        setFileType(fileExtension);
        setPreviewUrl(null);
        setIsFileSelected(false);
      } finally {
        setIsLoadingPreviewLocal(false);
        setSelectedFile({});
      }
    };

    fetchFilePreview();
  }, [isEditMode, jobHierarchyData]);

  const currentFile =
    Object.values(selectedFile)[0]?.file ||
    (jobHierarchyData && jobHierarchyData.file_name
      ? ({
          name: jobHierarchyData.file_name,
          size: jobHierarchyData.file_size
            ? parseFloat(jobHierarchyData.file_size) * 1024
            : 0,
          type: isPdfFile(fileType || "")
            ? "application/pdf"
            : isImageFile(fileType || "")
              ? `image/${fileType}`
              : "application/octet-stream",
          lastModified: Date.now(),
        } as unknown as File)
      : null);

  return (
    <>
      <Box sx={{ margin: "40px 0px" }} className={Styles.stepTopBox}>
        <Typography variant="h6" gutterBottom sx={{ color: "#000" }}>
          Job Hierarchy
        </Typography>
        <div className={Styles.uploadTab}>
          {(previewUrl || currentFile) && (
            <p onClick={handleDeleteFile} color="error">
              <img src={Delete} alt="Delete file" />
            </p>
          )}
          <button onClick={triggerFileUpload}>
            {previewUrl || currentFile ? "Change" : "Upload"}
          </button>
        </div>
      </Box>

      <Box
        className={Styles.uploadBox}
        sx={{ width: "100%", cursor: "pointer" }}
        onClick={triggerFileUpload}
      >
        <input
          id="fileUpload"
          type="file"
          style={{ display: "none" }}
          onChange={handleFileChange}
          accept=".jpeg,.jpg,.png,.pdf,.pptx,.docx,.doc,.ppt,.xlsx,.xls"
        />

        {isLoadingPreview ? (
          <div className={Styles.uploadDiv}>
            <Typography variant="body1">Loading file preview...</Typography>
          </div>
        ) : previewUrl ? (
          <>
            {isPdfFile(fileType || "") ? (
              <embed
                src={previewUrl}
                type="application/pdf"
                style={{ width: "100%", height: "298px" }}
              />
            ) : isImageFile(fileType || "") ? (
              <img
                src={previewUrl}
                alt="File preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "298px",
                  objectFit: "contain",
                }}
              />
            ) : null}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1">
                <strong>File name:</strong> {currentFile?.name}
              </Typography>
              <Typography variant="body1">
                <strong>File size:</strong>{" "}
                {currentFile?.size ? formatFileSize(currentFile.size) : "N/A"}
              </Typography>
            </Box>
          </>
        ) : currentFile ? (
          <div className={Styles.uploadDiv}>
            <Typography variant="h6" fontSize={20}>
              {fileType?.toUpperCase() || "Document"} File
            </Typography>
            <Typography variant="body1">
              <strong>File name:</strong> {currentFile.name}
            </Typography>
            <Typography variant="body1">
              <strong>File size:</strong> {formatFileSize(currentFile.size)}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {fileType && isOfficeFile(fileType)
                ? "Preview not available"
                : "File uploaded"}
            </Typography>
          </div>
        ) : (
          <div className={Styles.uploadDiv}>
            <img src={Upload} alt="Upload icon" />
            <Typography variant="body1" sx={{ color: "#0029FF" }}>
              Upload
            </Typography>
            <Typography variant="h6" fontSize={20}>
              Upload the Job Hierarchy File
            </Typography>
            <Typography variant="body1" color="textSecondary">
              File types: JPEG, JPG, PNG, PDF, PPTX, DOCX, DOC, PPT, XLSX, XLS,
              XLSM
            </Typography>
          </div>
        )}
      </Box>
    </>
  );
};

export default JobHierarchyUploadStep;
